import React, { useEffect, useState } from 'react';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import ytLogo from "../images/ytlogo.svg"; // Ensure YouTube logo exists
import tiktokLogo from "../images/tiktoklogo.svg"; // Ensure TikTok logo exists

const ReviewerDetails = ({ docId }) => {
  const [reviewer, setReviewer] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [videoPlatform, setVideoPlatform] = useState(null);
  const [externalUrl, setExternalUrl] = useState(null);

  useEffect(() => {
    if (!docId) return;

    const fetchReviewerDetails = async () => {
      try {
        const reviewRef = doc(db, "reviews", docId);
        const reviewDoc = await getDoc(reviewRef);

        if (!reviewDoc.exists()) {
          return;
        }

        const reviewData = reviewDoc.data();

        // Check if review has a video
        const hasVideo = reviewData.labels?.includes("has_video") || false;
        let detectedPlatform = null;
        let detectedUrl = reviewData.external_url || null;

        if (hasVideo && detectedUrl) {
          if (detectedUrl.includes("youtube")) {
            detectedPlatform = "youtube";
          } else if (detectedUrl.includes("tiktok")) {
            detectedPlatform = "tiktok";
          }
        }

        setVideoPlatform(detectedPlatform);
        setExternalUrl(detectedUrl);

        // Determine if the reviewer is verified based on labels
        const isVerified =
          reviewData.labels && reviewData.labels.includes("verified_user");

        // Extract reviewer details
        const reviewerDetails = {
          name: reviewData.reviewer_name || "Unknown Reviewer",
          operationDate: reviewData.timestamp
            ? new Date(reviewData.timestamp.seconds * 1000).toLocaleDateString()
            : "N/A", // Convert Firestore timestamp to readable date
          graftCount: reviewData.graft_count?.[0] || "N/A",
          clinic: reviewData.clinic_only_name || "N/A",
          doctor: reviewData.doctor_name?.trim()
            ? reviewData.doctor_name
            : "Unavailable",
          isVerified,
        };

        setReviewer(reviewerDetails);

        // Fetch the profile image from Firestore
        if (reviewData.logoImage && reviewData.logoImage[0]?.downloadURL) {
          setProfilePic(reviewData.logoImage[0].downloadURL);
        } else {
          //console.warn("Profile image not found.");
        }
      } catch (error) {
        //console.error("Error fetching reviewer details:", error);
      }
    };

    fetchReviewerDetails();
  }, [docId]);

  if (!reviewer) return null;

  return (
    <div className="bg-gray-100 py-6 px-4 shadow-md max-w-5xl mx-auto flex flex-col sm:flex-row items-center sm:justify-between">
      {/* Profile Picture */}
      <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-4">
        <img
          src={profilePic || "/default-profile.png"} // Default fallback image
          alt="Reviewer"
          className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-full border-gray-300 shadow-lg"
        />
      </div>

      {/* Reviewer Info */}
      <div className="flex-1 text-center sm:text-left">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          {reviewer.name}
          {reviewer.isVerified && (
            <span
              className="ml-2 text-green-600 inline-flex items-center relative"
              title="Our team reached out to this user to confirm the review is authentic" // Tooltip for desktop
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 16 16"
                className="w-4 h-4"
              >
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.854 11.354l5-5a.5.5 0 0 0-.708-.708L6.5 10.293 4.854 8.646a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0z" />
              </svg>
              <span className="ml-1 text-sm font-medium">Verified User</span>
            </span>
          )}
        </h3>
        <p className="text-gray-600 text-sm">Operation Date: {reviewer.operationDate}</p>
        <p className="text-gray-600 text-sm">Graft Count: {reviewer.graftCount}</p>
        <p className="text-gray-600 text-sm">Clinic: {reviewer.clinic}</p>
        <p className="text-gray-600 text-sm">Doctor: {reviewer.doctor}</p>
      </div>

      {/* Video Series Available */}
      {videoPlatform && externalUrl && (
        <div className="p-6 flex-shrink-0 flex flex-col items-center sm:ml-4">
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center hover:opacity-80"
          >
            <img
              src={videoPlatform === "youtube" ? ytLogo : tiktokLogo}
              alt={videoPlatform}
              className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
            />
            <p className="mt-2 text-gray-700 font-medium text-sm text-center hover:underline hover:cursor-pointer">
              Video Series Available!
            </p>
          </a>
        </div>
      )}
    </div>
  );
};

export default ReviewerDetails;
